'use strict';

import { cn_dist } from './cn_utilities';
import {
    cn_material_type,
    CN_MATERIAL_TYPE_FACING,
    CN_MATERIAL_TYPE_HEAVY_STRUCTURE,
    CN_MATERIAL_TYPE_INSULATED_STRUCTURE,
    CN_MATERIAL_TYPE_INSULATOR,
    CN_MATERIAL_TYPE_LIGHT_STRUCTURE
} from '../model/cn_material_type';
import { CODES_BIM_PARAMETRES_BATIMENT, CODES_BIM_PARAMETRES_ESPACES, CODES_BIM_PARAMETRES_OUVRAGES, ENUMS_PARAMETRES_OUVRAGES } from './cn_bbp_constants';
import { nearest_bbp_color_label } from './bbp_colors';
import { cn_building } from '../model/cn_building';
import { cn_storey } from '../model/cn_storey';
import { cn_space } from '../model/cn_space';
import { cn_opening } from '../model/cn_opening';
import { cn_object } from '../model/cn_object';
import { cn_slab } from '../model/cn_slab';
import { cn_pipe } from '../model/cn_pipe';
import { cn_roof_slab } from '../model/cn_roof_slab';
import { cn_stairs } from '../model/cn_stairs';
import { BUILDING_EXTENSION_DATA_ADMIN_CONTRACTING_AUTHORITY, BUILDING_EXTENSION_DATA_ENERGY_REF_ELEC, BUILDING_EXTENSION_DATA_ENERGY_REF_FUEL } from '../model/cn_building_data';
import { cn_wall_interface } from '../model/cn_wall_interface';

function truncateDecimals(number, digits = 2) {
    const multiplier = Math.pow(10, digits),
        adjustedNum = number * multiplier,
        truncatedNum = Math[adjustedNum < 0 ? 'ceil' : 'floor'](adjustedNum);

    return truncatedNum / multiplier;
}

export class BbpHelper {

    /**
     * @param {cn_building} building
     */
    getBuildingParameters(building) {
        const result = {};
        result[CODES_BIM_PARAMETRES_BATIMENT.constructionYear] = building.building_data.construction_date || null;
        result[CODES_BIM_PARAMETRES_BATIMENT.contractingAuthority] = building.building_data.extension_administrative[BUILDING_EXTENSION_DATA_ADMIN_CONTRACTING_AUTHORITY] || null;
        result[CODES_BIM_PARAMETRES_BATIMENT.energyRefElec] = building.building_data.extension_energy[BUILDING_EXTENSION_DATA_ENERGY_REF_ELEC] || null;
        result[CODES_BIM_PARAMETRES_BATIMENT.energyRefFuel] = building.building_data.extension_energy[BUILDING_EXTENSION_DATA_ENERGY_REF_FUEL] || null;
        return result;
    }

    /**
     * @param {cn_space} space
     * @param {cn_storey} storey
     */
    getSpaceParameters(space, storey) {
        const result = {};
        result[CODES_BIM_PARAMETRES_ESPACES.spacePerimeter] = truncateDecimals(space.perimeter);
        result[CODES_BIM_PARAMETRES_ESPACES.spaceHeight] = space.ceiling_type ? space.ceiling_height : storey.height;
        if (space.is_heated_relevant()) {
            result[CODES_BIM_PARAMETRES_ESPACES.spaceHeated] = space.is_heated();
        }
        return result;
    }

    /**
     *
     * @param {cn_wall_interface} wall_interface
     * @param {cn_storey} storey
     * @returns
     */
    getWallParameters(wall_interface, storey) {
        const result = {};
        const wall_type = wall_interface.wall.wall_type;
        const wall_metrics = wall_interface.get_metrics(storey);

        if (storey.exterior) {
            // --> Fence case
            result[CODES_BIM_PARAMETRES_OUVRAGES.fenceLength] = wall_metrics.axis_length;
            result[CODES_BIM_PARAMETRES_OUVRAGES.fenceThickness] = wall_metrics.thickness;
            result[CODES_BIM_PARAMETRES_OUVRAGES.fenceHeight] = wall_metrics.height_type;
            result[CODES_BIM_PARAMETRES_OUVRAGES.fenceArea] = wall_metrics.gross_side_exchange_area;
            result[CODES_BIM_PARAMETRES_OUVRAGES.fenceRailingHeight] = wall_metrics.height_type_fence;
        } else if (wall_interface.wall.balcony) {
            // --> Balcony case
            result[CODES_BIM_PARAMETRES_OUVRAGES.balconyThickness] = wall_metrics.thickness;
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallLength] = wall_metrics.axis_length;
            result[CODES_BIM_PARAMETRES_OUVRAGES.balconyHeight] = wall_metrics.height_type;
        } else {
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallThickness] = wall_metrics.thickness;
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallLength] = wall_metrics.axis_length;
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallHeight] = wall_metrics.height_bb;
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallArea] = wall_metrics.gross_side_exchange_area;
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallVolume] = wall_metrics.gross_volume;
            const layers = wall_type.layers;
            const uValue = wall_type.get_U();
            this.setWallInsulationParams(layers, result, uValue);
            const facingMaterial = layers.find(it => it.type === CN_MATERIAL_TYPE_FACING);
            if (facingMaterial && facingMaterial.code) {
                result[CODES_BIM_PARAMETRES_OUVRAGES.wallFacing] = ENUMS_PARAMETRES_OUVRAGES.wallFacings[facingMaterial.code];
            }
            const lightStructureMaterial = layers.find(it => it.type === CN_MATERIAL_TYPE_LIGHT_STRUCTURE);
            if (lightStructureMaterial && lightStructureMaterial.code) {
                result[CODES_BIM_PARAMETRES_OUVRAGES.wallLightStructure] = ENUMS_PARAMETRES_OUVRAGES.wallLightStructures[lightStructureMaterial.code];
            }
        }

        return result;
    }

    /**
     * @param {cn_opening} opening
     * @param {boolean} is_exterior
     * @param {boolean} is_roof_opening
     */
    getOpeningParameters(opening, is_exterior, is_roof_opening) {
        const result = {};
        if (is_exterior) {

            // outside opening (gate)

            result[CODES_BIM_PARAMETRES_OUVRAGES.gateThickness] = opening.thickness;
            result[CODES_BIM_PARAMETRES_OUVRAGES.gateWidth] = opening.get_width();
            result[CODES_BIM_PARAMETRES_OUVRAGES.gateHeight] = opening.get_height();


        } else {

            // inside opening (door, window, roof_opening)
            if (opening.thickness) {
                result[CODES_BIM_PARAMETRES_OUVRAGES.openingThickness] = opening.thickness;
            }

            let opening_type = opening.get_opening_type();
            result[CODES_BIM_PARAMETRES_OUVRAGES.openingWidth] = opening.get_width();
            if (is_roof_opening) {
                // @ts-ignore
                result[CODES_BIM_PARAMETRES_OUVRAGES.openingLenght] = opening.get_length();
            } else {
                result[CODES_BIM_PARAMETRES_OUVRAGES.openingHeight] = opening.get_height();
            }
            result[CODES_BIM_PARAMETRES_OUVRAGES.openingShift] = opening_type.z;
            result[CODES_BIM_PARAMETRES_OUVRAGES.openingPanels] = opening_type.panels;


            if (typeof (opening_type.get_area) == 'function')
                result[CODES_BIM_PARAMETRES_OUVRAGES.openingArea] = opening_type.get_area();

            if (typeof (opening_type.get_glazing_area) == 'function')
                result[CODES_BIM_PARAMETRES_OUVRAGES.openingGlazingArea] = opening_type.get_glazing_area();

            const openingFrame = ENUMS_PARAMETRES_OUVRAGES.openingFrames[opening_type.frame]
            if (!!openingFrame) {
                result[CODES_BIM_PARAMETRES_OUVRAGES.openingFrame] = openingFrame;
            }

            const openingGlazing = ENUMS_PARAMETRES_OUVRAGES.openingGlazings[opening_type.glazing];
            if (!!openingGlazing) {
                result[CODES_BIM_PARAMETRES_OUVRAGES.openingGlaze] = openingGlazing;
            }
            // Export glazing gaz only for double glazing
            if (opening_type.glazing == 'double') {
                const glazing_gaz = opening_type.glazing_gaz;
                let bbpGlazingGaz, bbpGlazingThickness;
                switch (glazing_gaz) {
                    case 'air_6': {
                        bbpGlazingGaz = 'Air';
                        bbpGlazingThickness = '6';
                        break;
                    }
                    case 'air_8': {
                        bbpGlazingGaz = 'Air';
                        bbpGlazingThickness = '8';
                        break;
                    }
                    case 'air_12': {
                        bbpGlazingGaz = 'Air';
                        bbpGlazingThickness = '12';
                        break;
                    }
                    case 'air_16': {
                        bbpGlazingGaz = 'Air';
                        bbpGlazingThickness = '16';
                        break;
                    }
                    case 'argon_16': {
                        bbpGlazingGaz = 'Argon';
                        bbpGlazingThickness = '16';
                        break;
                    }
                }
                result[CODES_BIM_PARAMETRES_OUVRAGES.openingGlazingGaz] = bbpGlazingGaz;
                result[CODES_BIM_PARAMETRES_OUVRAGES.openingGlazingThickness] = bbpGlazingThickness;
            }


            const openingType = ENUMS_PARAMETRES_OUVRAGES.openingTypes[opening_type.opening];
            if (!!openingType) {
                result[CODES_BIM_PARAMETRES_OUVRAGES.openingOpener] = openingType;
            }
            const openingTransom = ENUMS_PARAMETRES_OUVRAGES.openingTransoms[opening_type.transom];
            if (!!openingTransom) {
                result[CODES_BIM_PARAMETRES_OUVRAGES.openingTransom] = openingTransom;
            }
            if (opening_type.category === 'window') {
                const openingSill = ENUMS_PARAMETRES_OUVRAGES.openingSills[opening_type.sill];
                if (!!openingSill) {
                    result[CODES_BIM_PARAMETRES_OUVRAGES.openingSill] = openingSill;
                }
                const openingClosing = ENUMS_PARAMETRES_OUVRAGES.openingClosings[opening_type.closing];
                if (!!openingClosing) {
                    result[CODES_BIM_PARAMETRES_OUVRAGES.openingClosing] = openingClosing;
                }
            }

            result[CODES_BIM_PARAMETRES_OUVRAGES.openingUw] = opening_type.Uw;
            result[CODES_BIM_PARAMETRES_OUVRAGES.openingSw] = opening_type.Sw;
            result[CODES_BIM_PARAMETRES_OUVRAGES.openingTl] = opening_type.Tl;

        }

        return result;
    }

    /**
     * @param {cn_stairs} stair
     * @param {cn_storey} storey
     */
    getStairParameters(stair, storey) {
        const result = {};
        result[CODES_BIM_PARAMETRES_OUVRAGES.stairHeight] = stair.actual_stair_height;
        result[CODES_BIM_PARAMETRES_OUVRAGES.stairNumber] = stair.actual_stair_number;
        result[CODES_BIM_PARAMETRES_OUVRAGES.stairDepth] = stair.actual_stair_depth;
        result[CODES_BIM_PARAMETRES_OUVRAGES.stairWidth] = stair.stair_width;
        result[CODES_BIM_PARAMETRES_OUVRAGES.stairwayWidth] = stair.stair_width;
        let stairs_metrics = stair.get_metrics(storey);
        result[CODES_BIM_PARAMETRES_OUVRAGES.stairwayLength] = stairs_metrics.length;
        result[CODES_BIM_PARAMETRES_OUVRAGES.stairwayHeight] = stairs_metrics.height;
        result[CODES_BIM_PARAMETRES_OUVRAGES.stairwayArea] = stairs_metrics.area;
        return result;
    }

    /**
     * @param beam
     * @param {cn_storey} storey
     */
    getBeamParameters(beam, storey) {
        const result = {};
        result[CODES_BIM_PARAMETRES_OUVRAGES.beamWidth] = beam.element_type.width;
        result[CODES_BIM_PARAMETRES_OUVRAGES.beamThickness] = beam.element_type.thickness;
        // BB
        result[CODES_BIM_PARAMETRES_OUVRAGES.beamBBWidth] = beam.element_type.width;
        if (beam.vertices) {
            let a = beam.vertices[0]
            let b = beam.vertices[1]
            const distance = truncateDecimals(Math.sqrt(Math.pow(cn_dist(a, b), 2) + Math.pow(a[2] - b[2], 2)));
            result[CODES_BIM_PARAMETRES_OUVRAGES.beamBBLength] = distance;
        }
        if (beam.height !== undefined && beam.height !== null) {
            result[CODES_BIM_PARAMETRES_OUVRAGES.beamBBHeight] = beam.height ? truncateDecimals(beam.height) : truncateDecimals(storey.height);
        }
        const beamMaterial = ENUMS_PARAMETRES_OUVRAGES.beamMaterials[beam.element_type.material];
        if (!!beamMaterial) {
            result[CODES_BIM_PARAMETRES_OUVRAGES.beamMaterial] = beamMaterial;
        }
        const beamShape = ENUMS_PARAMETRES_OUVRAGES.beamShapes[beam.element_type.shape];
        if (!!beamShape) {
            result[CODES_BIM_PARAMETRES_OUVRAGES.beamShape] = beamShape;
        }
        return result;
    }

    /**
     * @param {cn_pipe} pipe
     */
    getPipeParameters(pipe) {
        const result = {};
        result[CODES_BIM_PARAMETRES_OUVRAGES.pipeDiameter] = pipe.element_type.diameter;

        let a = pipe.vertices[0]
        let b = pipe.vertices[1]
        if (a[0] === b[0]) {
            // Vertical
            result[CODES_BIM_PARAMETRES_OUVRAGES.pipeLength] = truncateDecimals(Math.abs(a[2] - b[2]));
        } else {
            result[CODES_BIM_PARAMETRES_OUVRAGES.pipeLength] = truncateDecimals(Math.sqrt(Math.pow(cn_dist(a, b), 2) + Math.pow(a[2] - b[2], 2)));
        }

        const pipeMaterial = ENUMS_PARAMETRES_OUVRAGES.pipeMaterials[pipe.element_type.material];
        if (!!pipeMaterial) {
            result[CODES_BIM_PARAMETRES_OUVRAGES.pipeMaterial] = pipeMaterial;
        }
        return result;
    }

    /**
     * @param {cn_slab} slab
     */
    getSlabParameters(slab) {
        const result = {};
        const slab_metrics = slab.get_metrics();
        result[CODES_BIM_PARAMETRES_OUVRAGES.slabThickness] = slab_metrics.thickness;
        result[CODES_BIM_PARAMETRES_OUVRAGES.slabWidth] = slab_metrics.width;
        result[CODES_BIM_PARAMETRES_OUVRAGES.slabLength] = slab_metrics.length;
        result[CODES_BIM_PARAMETRES_OUVRAGES.slabArea] = slab_metrics.area;
        result[CODES_BIM_PARAMETRES_OUVRAGES.slabVolume] = slab_metrics.volume;
        const layers = slab.slab_type.layers;
        const uValue = slab.slab_type.get_U();
        this.setWallInsulationParams(layers, result, uValue, true);
        return result;
    }

    /**
     * @param {cn_roof_slab} slab
     */
    getRoofParameters(slab) {
        const result = {};
        //*** full area  */
        const polygon = slab.build_3d_polygon(0, true);
        result[CODES_BIM_PARAMETRES_OUVRAGES.roofArea] = polygon.get_area();
        result[CODES_BIM_PARAMETRES_OUVRAGES.roofThickness] = slab.slab_type.thickness;
        return result;
    }

    /**
     * @param {cn_object} object
     */
    getObjectParameters(object) {
        const result = {};
        result[CODES_BIM_PARAMETRES_OUVRAGES.objectBBWidth] = object.size[0];
        result[CODES_BIM_PARAMETRES_OUVRAGES.objectBBLength] = object.size[1];
        result[CODES_BIM_PARAMETRES_OUVRAGES.objectBBHeight] = object.size[2];
        return result;
    }

    getWallFacingParameters(space_segment, side) {
        const result = {};
        const facing = space_segment.facing;
        if (facing) {
            if (space_segment.is_outdoor()) {
                result[CODES_BIM_PARAMETRES_OUVRAGES.exteriorWallFacing] = ENUMS_PARAMETRES_OUVRAGES.exteriorWallFacingTypes[facing.category];
            } else {
                result[CODES_BIM_PARAMETRES_OUVRAGES.interiorWallFacing] = ENUMS_PARAMETRES_OUVRAGES.interiorWallFacingTypes[facing.category];
            }
            result[CODES_BIM_PARAMETRES_OUVRAGES.building_color] = nearest_bbp_color_label(facing.color);
        }
        return result;
    }

    /**
     * @param {cn_space} space
     */
    getFloorFacingParameters(space) {
        const result = {};
        const facing = space.facings[0];
        if (facing) {
            result[CODES_BIM_PARAMETRES_OUVRAGES.floorFacing] = ENUMS_PARAMETRES_OUVRAGES.floorFacingTypes[facing.category];
            result[CODES_BIM_PARAMETRES_OUVRAGES.building_color] = nearest_bbp_color_label(facing.color);
        }
        return result;
    }

    /**
     * @param {cn_space} space
     */
    getCeilingFacingParameters(space) {
        const result = {};
        const facing = space.facings[1];
        if (facing) {
            result[CODES_BIM_PARAMETRES_OUVRAGES.ceilingFacing] = ENUMS_PARAMETRES_OUVRAGES.ceilingFacingTypes[facing.category];
            result[CODES_BIM_PARAMETRES_OUVRAGES.building_color] = nearest_bbp_color_label(facing.color);
        }
        return result;
    }

    /**
     *
     * @param {cn_material_type[]}layers
     * @param result
     * @param uValue
     * @param isSlab
     */
    setWallInsulationParams(layers, result, uValue, isSlab = false) {
        const structure = layers.find(layer => {
            return !!ENUMS_PARAMETRES_OUVRAGES.wallStructures[layer.code.replace('insulated_', '')];
        });
        if (structure) {
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallStructure] = ENUMS_PARAMETRES_OUVRAGES.wallStructures[structure.code.replace('insulated_', '')];
        }
        if (uValue) {
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallUValue] = uValue;
        }
        const insulationMaterials = layers.filter(it => it.type === CN_MATERIAL_TYPE_INSULATOR)
        const insulationMaterial1 = insulationMaterials.length >= 1 ? insulationMaterials[0] : null;
        const insulationMaterial2 = insulationMaterials.length >= 2 ? insulationMaterials[1] : null;
        const insulationMaterial1Code = insulationMaterial1 ? insulationMaterial1.code : null;
        const insulationMaterial1Width = insulationMaterial1 ? insulationMaterial1.thickness : null;
        const insulationMaterial2Code = insulationMaterial2 ? insulationMaterial2.code : null;
        const insulationMaterial2Width = insulationMaterial2 ? insulationMaterial2.thickness : null;
        if (insulationMaterial1) {
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationMaterial] = ENUMS_PARAMETRES_OUVRAGES.wallInsulators[insulationMaterial1Code];
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationWidth] = insulationMaterial1Width * 1000; // In millimeters in DATA
        } else {
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationMaterial] = ENUMS_PARAMETRES_OUVRAGES.wallInsulators.none;
        }
        if (insulationMaterial2) {
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationMaterial2] = ENUMS_PARAMETRES_OUVRAGES.wallInsulators[insulationMaterial2Code];
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationWidth2] = insulationMaterial2Width * 1000; // In millimeters in DATA
        } else {
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationMaterial2] = ENUMS_PARAMETRES_OUVRAGES.wallInsulators.none;
        }
        const insulatedStructures = layers.filter(l => l.type === CN_MATERIAL_TYPE_INSULATED_STRUCTURE);
        if (insulatedStructures.length) {
            result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationType] = ENUMS_PARAMETRES_OUVRAGES.wallInsulationTypes.integrated;
        } else {
            const structuresIndex = layers.map((l, i) => l.type === CN_MATERIAL_TYPE_HEAVY_STRUCTURE || l.type === CN_MATERIAL_TYPE_INSULATED_STRUCTURE ? i : -1).filter(i => i >= 0);
            const min = Math.min(...structuresIndex);
            const max = Math.max(...structuresIndex);
            const iti = layers.some((l, i) => l.type === CN_MATERIAL_TYPE_INSULATOR && i > max);
            const ite = layers.some((l, i) => l.type === CN_MATERIAL_TYPE_INSULATOR && i < min);
            if (isSlab) {
                // Slabs
                if (iti) {
                    result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationType] = ENUMS_PARAMETRES_OUVRAGES.wallInsulationTypes.inferior;
                } else if (ite) {
                    result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationType] = ENUMS_PARAMETRES_OUVRAGES.wallInsulationTypes.superior;
                } else {
                    result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationType] = ENUMS_PARAMETRES_OUVRAGES.wallInsulationTypes.none;
                }
            } else {
                // Walls
                if (ite && iti) {
                    result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationType] = ENUMS_PARAMETRES_OUVRAGES.wallInsulationTypes.divided;
                } else if (ite) {
                    result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationType] = ENUMS_PARAMETRES_OUVRAGES.wallInsulationTypes.exterior;
                } else if (iti) {
                    result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationType] = ENUMS_PARAMETRES_OUVRAGES.wallInsulationTypes.interior;
                } else {
                    result[CODES_BIM_PARAMETRES_OUVRAGES.wallInsulationType] = ENUMS_PARAMETRES_OUVRAGES.wallInsulationTypes.none;
                }
            }
        }
    }
}

